/**
 * ResendVerificationButton — autenticazione Phase 6 (IMPL-P6-003).
 *
 * UC-006 resend trigger. Used by {@link UnverifiedEmailBanner} (and reusable
 * elsewhere). On click it calls `authClient.resendVerification(email)` and
 * starts a 60-second cooldown regardless of API outcome — the cooldown is
 * always applied client-side to prevent abuse and keep the UX consistent
 * with the API's anti-enumeration response.
 *
 * Accessibility:
 *  - The countdown wrapper carries `aria-live="polite"` so SR users hear
 *    the "Reinvia tra Ns" updates without focus stealing.
 *  - The button is disabled while the cooldown is active.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { authClient } from '../lib/auth-client';

interface ResendVerificationButtonProps {
  email: string;
  /** Optional Tailwind classes to override the default ghost-style button. */
  className?: string;
}

const COOLDOWN_MS = 60_000;

export function ResendVerificationButton({ email, className }: ResendVerificationButtonProps) {
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [sending, setSending] = useState(false);

  // Tick a 1s setInterval ONLY while the cooldown is active so the SR-live
  // region announces a fresh remaining-seconds value each second.
  useEffect(() => {
    if (cooldownEndsAt === null) return;
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownEndsAt]);

  const remainingMs = cooldownEndsAt !== null ? Math.max(0, cooldownEndsAt - now) : 0;
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const cooldownActive = remainingMs > 0;

  // When the cooldown elapses, clear it so the next click can fire again.
  useEffect(() => {
    if (cooldownEndsAt !== null && remainingMs === 0) {
      setCooldownEndsAt(null);
    }
  }, [cooldownEndsAt, remainingMs]);

  async function handleClick() {
    if (cooldownActive || sending) return;
    setSending(true);
    try {
      await authClient.resendVerification(email);
    } catch {
      /* anti-enumeration: outcome is opaque to the user */
    } finally {
      // Always start the cooldown — the user gets identical UX whether the
      // server actually sent an email or not.
      setCooldownEndsAt(Date.now() + COOLDOWN_MS);
      setSending(false);
    }
  }

  const label = cooldownActive
    ? `Reinvia tra ${remainingSeconds}s`
    : sending
      ? 'Invio in corso…'
      : 'Reinvia link';

  return (
    <span aria-live="polite" className="inline-flex">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={cooldownActive || sending}
        className={className}
      >
        {label}
      </Button>
    </span>
  );
}
